import React, { useState } from 'react';
import { useForm, type SubmitHandler, Controller } from 'react-hook-form';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { Button } from '@/components/atoms/NewPcButton';
import { useQueryClient } from '@tanstack/react-query'; // 추가
import Input from '@/components/V3/Input';
import TextField from '@/components/molecules/TextField';

type FormData = {
  newAlias: string;
};

const UpdateMarketAlias = ({ marketNameEnglish, vendorId, marketId, currentAlias }) => {
  const user = useAppSelector((state) => state.user);
  const { register, control, watch, setValue, handleSubmit } = useForm<FormData>({
    defaultValues: {
      newAlias: currentAlias || '',
    },
  });
  const queryClient = useQueryClient(); // 추가

  // 별칭 중복확인 관련 상태
  const [aliasCheckStatus, setAliasCheckStatus] = useState<
    'idle' | 'checking' | 'valid' | 'duplicate'
  >('idle');
  const [aliasCheckMsg, setAliasCheckMsg] = useState('');

  // 별칭 중복확인 API
  const { mutate: checkAlias, isPending: isChecking } = usePost(CPLAT_API_ROUTES.updateMarketAlias);

  const handleCheckAlias = () => {
    const alias = watch('newAlias').trim();
    if (!alias) {
      setAliasCheckMsg('별칭을 입력해 주세요.');
      setAliasCheckStatus('idle');
      return;
    }
    setAliasCheckStatus('checking');
    checkAlias(
      {
        cplatToken: user.CplatToken,
        MarketName: marketNameEnglish,
        StoreId: marketNameEnglish === 'coupang' ? vendorId : marketId,
        originMarketAlias: currentAlias,
        toBeMarketAlias: alias,
      },
      {
        onSuccess: (res) => {
          if (res.data?.code === '200') {
            setAliasCheckStatus('valid');
            setAliasCheckMsg('별칭이 변경되었습니다.');
            queryClient.refetchQueries({ queryKey: [CPLAT_API_ROUTES.getRegisteredMarketInfo] });
          } else if (res.data?.code === '722') {
            setAliasCheckStatus('duplicate');
            setAliasCheckMsg('이미 사용 중인 별칭입니다.');
          } else {
            setAliasCheckStatus('idle');
            setAliasCheckMsg('별칭 확인 중 오류가 발생했습니다.');
          }
        },
        onError: () => {
          setAliasCheckStatus('idle');
          setAliasCheckMsg('별칭 확인 중 오류가 발생했습니다.');
        },
      },
    );
  };

  return (
    <div className="mt-[16px] flex flex-col  gap-2">
      <div className="flex w-full items-center gap-2">
        <div className="w-[100px] text-gr-800 Body2M16">별칭</div>
        <div className="flex flex-1  items-center gap-2">
          <Controller
            name="newAlias"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                className="h-[40px] Body5M14"
                placeholder="스토어 별칭을 입력하세요"
                maxLength={20}
                autoComplete="off"
              />
            )}
          />
          <Button
            size="xsmall"
            variant="secondary"
            type="button"
            className="h-[34px] w-[122px] shrink-0"
            onClick={handleCheckAlias}
            isLoading={isChecking}
          >
            별칭 저장
          </Button>
        </div>
      </div>
      {aliasCheckMsg && (
        <span
          className={`ml-2 text-sm ${
            aliasCheckStatus === 'valid'
              ? 'text-green-500'
              : aliasCheckStatus === 'duplicate'
              ? 'text-red-500'
              : 'text-gr-400'
          }`}
        >
          {aliasCheckMsg}
        </span>
      )}
    </div>
  );
};

export default UpdateMarketAlias;
